/**
 * Receipt renderers — convert ReceiptElement[] to platform-specific formats.
 *
 * toDantSuMarkup() → DantSu library formatted text for Android printing
 * [future] toEscPosBytes() → raw ESC/POS for iOS
 */
import type { ReceiptElement } from './receiptTypes'

/**
 * Convert ReceiptElement[] to DantSu formatted text markup.
 *
 * DantSu format reference:
 *   [L] = left align, [C] = center, [R] = right align
 *   <b>text</b> = bold
 *   <img>hex</img> = image
 *   <qrcode size='N'>url</qrcode> = QR code
 */
export function toDantSuMarkup(elements: ReceiptElement[], maxChars = 25): string {
  const lines: string[] = []

  for (const el of elements) {
    switch (el.type) {
      case 'text': {
        const align = el.align === 'center' ? '[C]' : el.align === 'right' ? '[R]' : '[L]'
        let text = el.text
        if (el.bold) text = `<b>${text}</b>`
        lines.push(`${align}${text}`)
        break
      }

      case 'columns': {
        let left = el.left
        let right = el.right
        if (el.leftBold) left = `<b>${left}</b>`
        if (el.rightBold) right = `<b>${right}</b>`
        lines.push(`[L]${left}[R]${right}`)
        break
      }

      case 'line': {
        const char = el.char ?? '_'
        lines.push(`[C]${char.repeat(maxChars)}`)
        break
      }

      case 'image': {
        lines.push(`[C]<img>${el.hex}</img>`)
        break
      }

      case 'qrcode': {
        // Native QR codes are sent as a printer command after the text (see getNativeQrCode).
        if (el.native) break
        const size = el.size ?? 25
        lines.push(`[C]<qrcode size='${size}'>${el.text}</qrcode>`)
        break
      }

      case 'chunkBreak':
        break

      case 'feed': {
        const count = el.lines ?? 1
        for (let i = 0; i < count; i++) {
          lines.push('')
        }
        break
      }
    }
  }

  return lines.join('\n')
}

/**
 * Convert ReceiptElement[] to the DantSu markup chunks (header, items, footer) that
 * PrinterManagerV2 sends separately with 50ms pauses to avoid printer buffer overflow.
 */
export function toDantSuChunks(elements: ReceiptElement[], maxChars = 25): string[] {
  const chunks: ReceiptElement[][] = [[]]
  for (const el of elements) {
    if (el.type === 'chunkBreak') chunks.push([])
    else chunks[chunks.length - 1].push(el)
  }
  return chunks.filter(chunk => chunk.length > 0).map(chunk => toDantSuMarkup(chunk, maxChars))
}

/** The URL the printer firmware prints as a QR code after the text chunks, if any. */
export function getNativeQrCode(elements: ReceiptElement[]): string | undefined {
  for (const el of elements) {
    if (el.type === 'qrcode' && el.native) return el.text
  }
  return undefined
}
